import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import {
  Box,
  Flex,
  useToast
} from "@chakra-ui/react";
import '../styles/dsr-display.scss';
import { updateDsr, editDsrTeamMembers, editDsrTeamMembersReset } from '../actions/dsr-actions';
import { getThisUserIdToken, redirectToNewPage, triggerLaunchdeckToast } from '../../global/helpers/global-helpers';
import PageLoader from '../../global/components/helper-components/components/PageLoader';
import LaunchdeckButton from '../../global/components/helper-components/components/LaunchdeckButton';
import LaunchdeckModal from '../../global/components/helper-components/components/LaunchdeckModal';



function DsrConfirmRemoveMemberModal({ modalTransferPayload, handleCloseModal, handleChangeUserActionRequired,
  // Actions
  updateDsr,
  editDsrTeamMembers,
  editDsrTeamMembersReset,
  // Redux
  dsrConfiguration, updateDsrOutcome, editDsrTeamMembersOutcome,
  isMobileView }) {

  const { removedUserId, role } = modalTransferPayload || {}
  const { dsrId, dsrOwnerId } = dsrConfiguration || {};
  const thisUserId = getThisUserIdToken();
  const isDsrOwner = (dsrOwnerId && (dsrOwnerId === thisUserId)) ? true : false;
  const isRemovingOwner = (removedUserId && (removedUserId === dsrOwnerId)) ? true : false;
  const isRemovingSelf = (removedUserId === thisUserId) ? true : false;


  // UX Management
  const toast = useToast();
  const [showLoader, setShowLoader] = useState(false)

  // Determine team
  const teamToUpdate = (role === 'buyer-member') ? "buyer-team" : "seller-team";

  useEffect(() => {
    if (updateDsrOutcome) {
      const { updateDsrStatus, sourceEvent } = updateDsrOutcome || {};
      setShowLoader(false);

      if (updateDsrStatus && sourceEvent === "remove-dsr-member") {
        handleCloseModal()
      }
    }

  }, [updateDsrOutcome, handleCloseModal]);

  useEffect(() => {
    if (editDsrTeamMembersOutcome) {
      const { updatedTeamMembers, removeUserAccessToRoom, statusMessage } = editDsrTeamMembersOutcome || {}

      if (removeUserAccessToRoom === true) {
        redirectToNewPage('/home')
      }

      if (updatedTeamMembers === true) {
        setShowLoader(false)
        handleChangeUserActionRequired('view-team-members', null)

        toast(
          triggerLaunchdeckToast({
            useCase: "show-success-state",
            bannerText: "Room member removed",
            label: statusMessage,
            isMobileView
          })
        )

      } else {
        handleCloseModal();

        toast(
          triggerLaunchdeckToast({
            useCase: "show-error-state",
            bannerText: "Unexpected error",
            label: "Member cannot be removed at this time. Please try again later",
            isMobileView
          })
        )
      }

      editDsrTeamMembersReset()
    }

  }, [editDsrTeamMembersOutcome, toast, handleCloseModal, handleChangeUserActionRequired]);

  const handleConfirmRemoveMember = () => {
    setShowLoader(true);

    const updateParams = {
      teamToUpdate, // Need to decide whether to remove it from the buying or the selling team
      updateAction: 'remove-member',
      userId: removedUserId // userId of the person who is picked
    }
    const updateTeamParams = { dsrId, updateParams }

    editDsrTeamMembers(updateTeamParams)
  }

  const handleConfirmRemoveOwner = () => {
    setShowLoader(true);

    // Instead of changing members, hange the status of the DSR to 'archived'
    // Archive status allows team members to continue viewing information inside DSR but not take any actions
    updateDsr({
      dsrId,
      status: 'archived',
      sourceEvent: "remove-dsr-member"
    })
  }

  function displayContent() {
    if (showLoader) {
      return (
        <Box w="100%" align="center" >
          <Box px='1em'>
            <Box>
              <PageLoader />
            </Box>
          </Box>
        </Box>
      )
    }

    if (!showLoader) {
      if (isDsrOwner && isRemovingOwner) {
        return (

          <Box w='100%' p='1em 1.8em'>
            <Flex w="100%" fontSize='0.875em' color='brandDark.500'>
              As the owner of this Room, are you sure about removing yourself and archiving this Room?
            </Flex>
            {/* // Future Scope: Allowing owner to nominate a new owner for the Room */}

            <Flex mt='1.625em' w='100%' justify={['center', 'flex-end']}>
              <LaunchdeckButton bg='brandPink.500' w={['100%', 'auto']} color='gray.50' fontSize='0.8em' py='0.4em' onClick={() => handleConfirmRemoveOwner()} label='Confirm' />
            </Flex>
          </Box>
        )
      }

      if (isRemovingOwner && !isDsrOwner) {
        return (
          <Box w='100%' p='1em 1.8em'>
            <Flex w="100%" fontSize='0.875em' color='brandDark.500'>
              You are trying to remove the owner of this Room. Please contact them about removing themselves from the Room if necessary
            </Flex>

            <Flex mt='1.625em' w='100%' justify={['center', 'flex-end']}>
              <LaunchdeckButton bg='brandDark.500' w={['100%', 'auto']} color='gray.50' fontSize='0.8em' py='0.4em' onClick={() => handleChangeUserActionRequired('view-team-members', null)} label='Back' />
            </Flex>
          </Box>
        )
      }

      // Default scenario
      return (
        <Box w='100%' p='1em 1.8em'>
          <Flex w="100%" fontSize='0.875em' color='brandDark.500'>
            {(isRemovingSelf && !isDsrOwner) ?
              <Box>
                Are you sure about removing yourself from this Room?
              </Box>
              :
              <Box>
                Are you sure about removing this member? They will no longer have access to the Room.
              </Box>
            }
          </Flex>

          <Flex mt='1.625em' w='100%' justify={['center', 'flex-end']}>
            <LaunchdeckButton bg='brandPink.500' w={['100%', 'auto']} color='gray.50' fontSize='0.8em' py='0.4em' onClick={() => handleConfirmRemoveMember()} label='Yes, remove' />
          </Flex>
        </Box>
      )
    }
  }

  return (

    <LaunchdeckModal
      title={isRemovingOwner ? "Leave Room" : "Remove member"}
      handleCloseModal={() => handleCloseModal()}
      showModal={true}
      maxW='35em'
    >
      {displayContent()}
    </LaunchdeckModal>

  )
}

function mapStateToProps(state) {
  return {
    updateDsrOutcome: state.updateDsrOutcome.results.data,
    dsrConfiguration: state.dsrConfiguration.results.data,
    editDsrTeamMembersOutcome: state.editDsrTeamMembersOutcome.results.data,
    isMobileView: state.mobileViewMode.isMobileView
  };
}


const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateDsr,
      editDsrTeamMembers,
      editDsrTeamMembersReset,
      // Remove User Confirmation
      // Checks if user is the owner. If so, warn that he will close the DSR
    },
    dispatch,
  )


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DsrConfirmRemoveMemberModal));
